import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Dialog, Slide, IconButton } from '@material-ui/core';
import clsx from 'clsx';
import FullScreenLoader from 'components/milestones/FullScreenLoader';
import CloseIcon from 'public/icons/close-white.svg';
import styles from 'styles/components/influencerSetup/InventoryItemModal.module.scss';
import { closeModal, openModal } from 'features/utilsSlice';
import useModalOpen from 'hooks/useModalOpen';
import { putRequest } from 'utils/api';
import CameraSignaturePreview from 'components/influencerSetup/CameraSignaturePreview';
import {
  openInventoryModal,
  selectInfluencerNetwork,
  updateInventory,
} from 'features/influencerSetupSlice';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} />;
});

const marginRight = { marginRight: 'auto' };

const useStyles = makeStyles(theme => ({
  paper: {
    height: '100%',
    marginTop: 'auto',
    position: 'relative',

    [theme.breakpoints.up('md')]: {
      height: '100%',
    },

    color: 'white',
    backgroundColor: 'rgba(50, 54, 67, .85) !important',
  },

  backdrop: {
    backgroundColor: 'rgba(50, 54, 67, .85) !important',
  },

  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    position: 'relative',
  },

  video: {
    width: '100%',
    height: 'auto',
    pointerEvents: 'none',
    position: 'relative',
    top: 0,
    left: 0,
    maxHeight: '30%',
    zIndex: 99,
  },

  canvas: {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    position: 'absolute',
    width: '100%',
    height: '100%',
    zIndex: 100,
  },

  button: {
    position: 'absolute',
    bottom: 80,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 60,
    height: 60,
    borderRadius: '100%',
    background: '#fff',
    outline: 'none',
    borderWidth: 0,
    zIndex: 100,
  },

  border: {
    position: 'absolute',
    bottom: 67.5,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 85,
    height: 85,
    borderRadius: '100%',
    background: 'transparent',
    border: '4px solid #D8D8D8',
    padding: 10,
    zIndex: 99,
  },

  closeButton: {
    position: 'absolute',
    top: 10,
    left: 10,
    zIndex: 999,
    padding: 5,
  },

  desc: {
    position: 'absolute',
    top: 80,
    textAlign: 'center',
    width: '100%',
    fontFamily: 'Avenir Heavy',
    fontSize: 16,
    bottom: 100,
    zIndex: 100,

    [theme.breakpoints.up('xs')]: {
      bottom: 150,
    },
  },
}));

const hdConstraints = {
  audio: false,
  video: {
    facingMode: 'environment',
    width: { ideal: 1280 },
    height: { ideal: 720 },
  },
};

const SignatureCamera = ({ handleClose, classes, fetchInventories }) => {
  const dispatch = useDispatch();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const tempRef = useRef(null);
  const cropAreaRef = useRef(null);
  const [mounted, setMounted] = useState(false);
  const [ready, setReady] = useState(false);
  const [progress, setProgress] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const inventory = useSelector(state => state.utils.modalData['Signature Camera']?.inventory);

  const createOverlay = () => {
    let scale = window.innerWidth / videoRef.current.videoWidth;
    let visible_height = window.innerHeight / scale;

    const canvas = canvasRef.current;

    // clear canvas
    let ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = 'rgba(18, 223, 199, 1.0)';
    ctx.lineWidth = 13;
    ctx.fillStyle = 'rgba(50, 54, 67, 0.85)';

    // 1. intialize constant
    const width_margin = 50;
    const width = canvas.width - width_margin * 2;
    const height = (width * 9) / 16;
    const height_margin = Math.max(0, (visible_height - height) / 2);
    const radius = 24;
    const margin = 17;
    const line_length = 40;
    const inner_radius = 15;

    // 3. draw background
    ctx.beginPath();
    ctx.roundRect(width_margin + width, height_margin, -width, height, inner_radius);
    ctx.rect(0, 0, canvas.width, canvas.height);
    ctx.fill();

    // 4. draw round green rectangle
    // left-top
    let x = width_margin - margin + radius;
    let y = height_margin - margin + radius;
    ctx.beginPath();
    ctx.arc(x, y, radius, Math.PI, 1.5 * Math.PI);
    ctx.moveTo(x, y - radius);
    ctx.lineTo(x + line_length, y - radius);
    ctx.moveTo(x - radius, y);
    ctx.lineTo(x - radius, y + line_length);
    ctx.stroke();
    // right-top
    x = canvas.width - width_margin + margin - radius;
    y = height_margin - margin + radius;
    ctx.beginPath();
    ctx.arc(x, y, radius, 1.5 * Math.PI, 0);
    ctx.moveTo(x, y - radius);
    ctx.lineTo(x - line_length, y - radius);
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + radius, y + line_length);
    ctx.stroke();
    // left-bottom
    x = width_margin - margin + radius;
    y = visible_height - height_margin + margin - radius;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0.5 * Math.PI, 1.0 * Math.PI);
    ctx.moveTo(x - radius, y);
    ctx.lineTo(x - radius, y - line_length);
    ctx.moveTo(x, y + radius);
    ctx.lineTo(x + line_length, y + radius);
    ctx.stroke();
    // right-bottom
    x = canvas.width - width_margin + margin - radius;
    y = visible_height - height_margin + margin - radius;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, 0.5 * Math.PI);
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + radius, y - line_length);
    ctx.moveTo(x, y + radius);
    ctx.lineTo(x - line_length, y + radius);
    ctx.stroke();

    cropAreaRef.current = { x: width_margin, y: height_margin, width: width, height: height };
  };

  const handleLoad = useCallback(() => {
    canvasRef.current.width = videoRef.current.videoWidth;
    canvasRef.current.height = videoRef.current.videoHeight;

    createOverlay();

    const track = videoRef.current.srcObject.getVideoTracks()[0];

    if (track != null) {
      let capabilities = track.getCapabilities();

      if (capabilities.torch) {
        track.applyConstraints({
          advanced: [{ torch: true }],
        });
      }
    }
  }, []);

  useEffect(() => {
    setMounted(true);

    const video = videoRef.current;

    return () => {
      setMounted(false);
      video.srcObject.getTracks().forEach(track => track.stop());
    };
  }, []);

  useEffect(() => {
    if (!mounted) return;

    navigator.mediaDevices.getUserMedia(hdConstraints).then(stream => {
      videoRef.current.srcObject = stream;
      videoRef.current.play();
      setReady(true);
    });

    const video = videoRef.current;

    video.addEventListener('loadedmetadata', handleLoad);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoad);
    };
  }, [mounted, handleLoad]);

  const handleCapture = () => {
    setIsLoading(true);

    const canvas = tempRef.current;

    canvas.width = cropAreaRef.current.width;
    canvas.height = cropAreaRef.current.height;

    let ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, -cropAreaRef.current.x, -cropAreaRef.current.y);

    canvas.toBlob(blob => {
      const file = new File([blob], `signature-${Date.now()}.png`, { type: 'image/png' });
      handleSubmit(file);
    });
  };

  const handleSubmit = file => {
    const data = new FormData();

    data.set('inventory[media]', file);

    putRequest({
      endpoint: `inventories/${inventory.id}`,
      data,
      config: {
        onUploadProgress: progressEvent => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          if (percentCompleted < 100) {
            setProgress(percentCompleted);
          }
        },
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    })
      .then(response => {
        setIsLoading(false);
        dispatch(updateInventory(response.data));
        dispatch(
          openModal({
            name: 'Signature Preview',
            data: { images: response.data.assets.map(a => a.url) },
          })
        );
        dispatch(openModal({ name: 'Signature Camera', data: { inventory: response.data } }));
        setProgress(100);
      })
      .catch(() => setIsLoading(false))
      .finally(() => {
        setProgress(1);
        createOverlay();
      });
  };

  const inventoryModalData = inventorySpace => {
    const formattedInventory = {
      ...inventory,
      inventory_items: [...inventory.inventory_items]
        .sort((a, b) => b.default - a.default)
        .map((item, index) => ({ ...item, index })),
    };

    return { inventory: formattedInventory, inventorySpace };
  };

  const openInventory = () => {
    dispatch(selectInfluencerNetwork(inventory.influencer_network));
    dispatch(openInventoryModal(inventoryModalData(inventory.inventory_space)));
    fetchInventories();
  };

  return (
    <div
      className={clsx(classes.paper, styles.modalContent, classes.backdrop)}
      style={{ padding: 0, overflow: 'hidden' }}
    >
      <div className={classes.container}>
        <IconButton
          edge="start"
          color="inherit"
          onClick={handleClose}
          aria-label="close"
          className={classes.closeButton}
        >
          <CloseIcon style={marginRight} width={15} height={15} />
        </IconButton>

        <video id="video" ref={videoRef} className={classes.video} playsInline />
        <canvas id="canvas" ref={canvasRef} className={classes.canvas} />
        <canvas id="temp" ref={tempRef} style={{ position: 'absolute', visibility: 'hidden' }} />

        {ready && (
          <>
            <button onClick={handleCapture} className={classes.button}></button>
            <div className={classes.border}></div>

            <div className={classes.desc}>
              Use black marker on white paper
              <br />
              Have nothing else in frame
              <br />
              Don't show paper edges
            </div>
          </>
        )}
      </div>

      {mounted &&
        isLoading &&
        createPortal(
          <FullScreenLoader progress={progress} />,
          document.getElementById('full-screen-loader-portal')
        )}

      <CameraSignaturePreview openInventory={openInventory} />
    </div>
  );
};

const SignatureCameraModal = props => {
  const dispatch = useDispatch();
  const open = useModalOpen('Signature Camera');
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const classes = useStyles();

  const handleClose = () => {
    dispatch(closeModal('Signature Camera'));
  };

  if (!fullScreen) return null;

  return (
    <Dialog
      fullScreen={fullScreen}
      open={open}
      TransitionComponent={Transition}
      className={clsx(styles.dialog, classes.paper, classes.backdrop)}
      classes={{ paper: classes.paper }}
    >
      {open && <SignatureCamera classes={classes} handleClose={handleClose} {...props} />}
      <div id="full-screen-loader-portal"></div>
    </Dialog>
  );
};

export default SignatureCameraModal;
